import React, { useState, useEffect } from "react";
import {
  FiBarChart2,
  FiFileText,
  FiUsers,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown,
  FiSettings,
} from "react-icons/fi";
import { BsTelephoneInbound, BsTelephoneOutbound } from "react-icons/bs";
import { AiOutlineFieldTime } from "react-icons/ai";
import { TfiTimer } from "react-icons/tfi";

import { useNavigate, useLocation } from "react-router-dom";
import { TbApi } from "react-icons/tb";
import { FaUserCog } from "react-icons/fa";

const Sidebar = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  // Función para alternar el estado de la barra lateral
  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Opciones del menú
  const menuItems = [
    {
      name: "Analytics",
      icon: <FiBarChart2 size={20} />,
      subItems: [
        {
          name: "Inbound",
          icon: <BsTelephoneInbound size={16} />,
          route: "/analytics/inbound",
        },
        {
          name: "Outbound",
          icon: <BsTelephoneOutbound size={16} />,
          route: "/analytics/outbound", // Puedes agregar esta ruta si es necesaria
        },
      ],
    },
    {
      name: "Call Detail Records",
      icon: <FiFileText size={20} />,
      route: "/cdr",
    },
    {
      name: "Realtime Monitor",
      icon: <TfiTimer size={20} />,
      route: "/monitor",
    },
    {
      name: "Management",
      icon: <FiSettings size={20} />,
      subItems: [
        {
          name: "Users and Permissions",
          icon: <FaUserCog size={16} />,
          route: "/management/users",
        },
        {
          name: "API",
          icon: <TbApi size={16} />,
          route: "/management/api",
        },
      ],
    },
  ];

  // Estado para el elemento seleccionado
  const [selectedItem, setSelectedItem] = useState("");

  useEffect(() => {
    // Marcar como seleccionado según la ruta actual
    const currentRoute = location.pathname;
    const currentItem = menuItems
      .flatMap((item) => item.subItems || item) // Aplana el menú para buscar subItems
      .find((item) => item.route === currentRoute);

    if (currentItem) {
      setSelectedItem(currentItem.name);
    }
  }, [location.pathname, menuItems]);

  // Toggle del menú expandido
  const toggleMenu = (menuName) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuName]: !prev[menuName],
    }));
  };

  // Renderización del menú
  const renderMenu = (items) => (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.name}>
          <div
            className={`flex items-center justify-between px-2 rounded-lg cursor-pointer transition ${
              selectedItem === item.name
                ? "font-semibold bg-secondary-light dark:bg-secondary-dark text-accent-light dark:text-accent-dark border-l-4 border-accent-light dark:border-accent-dark"
                : "hover:bg-secondary-light dark:hover:bg-secondary-dark"
            }`}
            onClick={() => {
              if (item.subItems) {
                toggleMenu(item.name);
              } else {
                setSelectedItem(item.name);
                navigate(item.route); // Navegar a la ruta correspondiente
              }
            }}
          >
            <div className="flex items-center gap-1">
              <div className="w-10 h-10 flex items-center justify-center">
                {item.icon}
              </div>
              {isSidebarOpen && <span className="text-md">{item.name}</span>}
            </div>
            {isSidebarOpen && item.subItems && (
              <span>
                {expandedMenus[item.name] ? (
                  <FiChevronDown size={16} />
                ) : (
                  <FiChevronRight size={16} />
                )}
              </span>
            )}
          </div>
          {item.subItems && expandedMenus[item.name] && (
            <ul className="space-y-1 mt-2 ml-4">
              {item.subItems.map((subItem) => (
                <li key={subItem.name}>
                  <div
                    className={`flex items-center group gap-4 p-2 rounded-lg cursor-pointer text-sm transition ${
                      selectedItem === subItem.name
                        ? "bg-panel-light dark:bg-panel-dark text-accent-light dark:text-accent-dark font-semibold"
                        : "hover:bg-secondary-light dark:hover:bg-secondary-dark"
                    }`}
                    onClick={() => {
                      setSelectedItem(subItem.name);
                      navigate(subItem.route); // Navegar a la ruta correspondiente
                    }}
                  >
                    <div className="flex items-center justify-center w-4 h-4 group ml-2">
                      {subItem.icon}
                    </div>
                    {isSidebarOpen && <span>{subItem.name}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <div
      className={`transition-all duration-300 ${
        isSidebarOpen ? "w-64" : "w-16"
      } bg-primary-light dark:bg-primary-dark text-text-primary-light dark:text-text-primary-dark`}
    >
      <button
        className="p-2 m-2 rounded-md bg-secondary-light dark:bg-secondary-dark hover:bg-accent-light dark:hover:bg-accent-dark transition flex items-center justify-center group"
        onClick={toggleSidebar}
        aria-label={
          isSidebarOpen ? "Cerrar barra lateral" : "Abrir barra lateral"
        }
      >
        {isSidebarOpen ? (
          <FiChevronLeft
            size={20}
            className="text-text-primary-light dark:text-text-primary-dark group-hover:text-white"
          />
        ) : (
          <FiChevronRight
            size={20}
            className="text-text-primary-light dark:text-text-primary-dark group-hover:text-white"
          />
        )}
      </button>
      <nav className="mt-4 overflow-y-auto scrollbar-thin scrollbar-thumb-secondary-light dark:scrollbar-thumb-secondary-dark scrollbar-thumb-rounded-lg">
        {renderMenu(menuItems)}
      </nav>
    </div>
  );
};

export default Sidebar;
